import { Button, Modal, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  VehicleModelCreateInput,
  VehicleModelUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { VehicleModelDTO } from "../api/client";
import {
  useCatalogVehicleMakeQuery,
  useCatalogVehicleModelsQuery,
  useCreateVehicleModel,
  useDeleteVehicleModel,
  useUpdateVehicleModel,
} from "../api/queries";
import { VehicleModelForm } from "../ui/VehicleModelForm";
import { VehicleModelsTable } from "../ui/VehicleModelsTable";

const DEFAULT_MODEL_LIMIT = 20;

export function CatalogVehicleMakeDetailPage() {
  const nav = useNavigate({ from: "/app/catalog/vehicles/$makeId" });
  const { makeId } = useParams({ from: "/app/catalog/vehicles/$makeId" });
  const [modelPage, setModelPage] = useState({
    limit: DEFAULT_MODEL_LIMIT,
    offset: 0,
  });

  const canCreate = useActionPermission(["catalog:create"]);
  const canManage = useActionPermission(
    ["catalog:update", "catalog:delete"],
    "any",
  );

  const modelLimit = modelPage.limit;
  const modelOffset = modelPage.offset;

  const makeQuery = useCatalogVehicleMakeQuery(makeId ?? "");
  const modelsQuery = useCatalogVehicleModelsQuery({
    makeId: makeId ?? undefined,
    limit: modelLimit,
    offset: modelOffset,
  });

  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelSubmitting, setModelSubmitting] = useState(false);
  const [editingModel, setEditingModel] = useState<VehicleModelDTO | null>(
    null,
  );

  const createModel = useCreateVehicleModel();
  const updateModel = useUpdateVehicleModel();
  const deleteModel = useDeleteVehicleModel();

  const make = makeQuery.data ?? null;
  const listResult = modelsQuery.data;
  const modelRows = listResult?.data ?? [];
  const modelMeta = listResult?.meta ?? {
    total: 0,
    limit: modelLimit,
    offset: modelOffset,
  };

  const makeOptions = useMemo(
    () => (make ? [{ value: make.id, label: make.name }] : []),
    [make],
  );

  if (!makeId) {
    return (
      <Main>
        <p className="text-muted-foreground">ບໍ່ພົບລາຍລະອຽດຍີ່ຫໍ້</p>
      </Main>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ລົດ", to: "/app/catalog/vehicles" },
            { label: make?.name ?? "ກຳລັງໂຫຼດ..." },
          ]}
        />
      </Header>

      <Main className="space-y-6">
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              {make?.name ?? "ກຳລັງໂຫຼດ..."}
            </h2>
            <p className="text-muted-foreground text-sm">
              ຈັດການຮຸ່ນ (Models) — ກົດຈັດການ Trims ໃນແຕ່ລະຮຸ່ນ
            </p>
          </div>
          {canCreate && make && (
            <Button
              onClick={() => {
                setEditingModel(null);
                setModelModalOpen(true);
              }}
            >
              <PlusIcon className="size-4" />
              ເພີ່ມຮຸ່ນ
            </Button>
          )}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-2 px-2">
            <h3 className="font-semibold text-base">ຮຸ່ນລົດ (Models)</h3>
          </div>

          <VehicleModelsTable
            canManage={!!canManage}
            isLoading={modelsQuery.isLoading}
            data={modelRows}
            offset={modelMeta.offset}
            limit={modelMeta.limit}
            totalCount={modelMeta.total}
            onPaginationChange={(offset, limit) =>
              setModelPage({ offset, limit })
            }
            onEdit={(row) => {
              setEditingModel(row);
              setModelModalOpen(true);
            }}
            onDelete={async (id) => {
              try {
                await deleteModel.mutateAsync(id);
                toast.success("ລຶບຮຸ່ນສຳເລັດ");
                await modelsQuery.refetch();
              } catch (err: unknown) {
                const msg =
                  err && typeof err === "object" && "message" in err
                    ? String((err as { message: string }).message)
                    : "ມີ trim ອ້າງອີງຢູ່ ບໍ່ສາມາດລຶບໄດ້";
                toast.error(msg);
              }
            }}
            onManageTrims={(modelId) =>
              nav({ to: `/app/catalog/vehicles/${makeId}/models/${modelId}` })
            }
          />
        </div>
      </Main>

      <Modal
        open={modelModalOpen}
        onOpenChange={(open) => {
          setModelModalOpen(open);
          if (!open) setEditingModel(null);
        }}
        title={editingModel ? "ແກ້ໄຂຮຸ່ນ" : "ເພີ່ມຮຸ່ນ"}
        size="sm"
      >
        <VehicleModelForm
          key={editingModel?.id ?? "new"}
          initialValues={
            editingModel
              ? {
                  makeId: editingModel.makeId,
                  name: editingModel.name,
                }
              : makeId
                ? { makeId, name: "" }
                : undefined
          }
          makeOptions={makeOptions}
          onSubmit={async (vals) => {
            try {
              setModelSubmitting(true);
              if (editingModel) {
                await updateModel.mutateAsync({
                  id: editingModel.id,
                  input: vals as VehicleModelUpdateInput,
                });
                toast.success("ແກ້ໄຂຮຸ່ນສຳເລັດ");
              } else {
                await createModel.mutateAsync(vals as VehicleModelCreateInput);
                toast.success("ເພີ່ມຮຸ່ນສຳເລັດ");
              }
              setModelModalOpen(false);
              setEditingModel(null);
              await modelsQuery.refetch();
            } catch (err: unknown) {
              const code = (err as { error?: string })?.error;
              if (code === "MODEL_CODE_DUPLICATE") {
                toast.error("Model Code ນີ້ມີໃນຍີ່ຫໍ້ນີ້ແລ້ວ");
              } else if (code === "MODEL_CODE_INVALID") {
                toast.error("Model Code ບໍ່ຖືກຕ້ອງ (A-Z, 0-9, -)");
              } else {
                toast.error(
                  (err as { message?: string })?.message ?? "ບັນທຶກບໍ່ສຳເລັດ",
                );
              }
            } finally {
              setModelSubmitting(false);
            }
          }}
          onCancel={() => setModelModalOpen(false)}
          submitting={modelSubmitting}
        />
      </Modal>
    </>
  );
}
